import { Link } from "react-router-dom";
import { Megaphone, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";
import MeshBg from "@/components/MeshBg";
import TypeWord from "@/components/TypeWord";
import { HOME, BRANDS, ASSETS, CAMPANII } from "@/data/content";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO — full width text */}
      <section className="relative overflow-hidden pt-16 md:pt-24 pb-12 md:pb-16">
        <MeshBg />
        <div className="container-x">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2 text-[13px] font-semibold text-slate-700 shadow-sm backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-brand" />
              {HOME.heroKicker}
            </span>
          </Reveal>
          <Reveal delay={0.06}>
            <h1 className="mt-7 text-[2rem] leading-[1.08] sm:text-4xl md:text-5xl lg:text-[3.2rem] xl:text-[4rem] font-extrabold tracking-tight">
              <span className="block">Povestitorul brandurilor care vor să</span>
              <span className="block">
                <TypeWord /> <span className="text-brand">tinerii</span>
              </span>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-3xl lead text-xl">{HOME.heroText}</p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#branduri" data-testid="hero-cta-brands" className="btn-primary">
                {HOME.heroCta}
              </a>
              <Link to="/ce-oferim" data-testid="hero-cta-offer" className="btn-secondary">
                Ce oferim
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS BAR — chenare mai mici */}
      <section className="border-y border-slate-100 bg-white">
        <div className="container-x grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
          {HOME.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="py-6 sm:px-8 first:sm:pl-0">
              <div className="text-3xl md:text-4xl font-extrabold tracking-tight text-ink">
                {s.value}
              </div>
              <div className="mt-1.5 text-slate-500 font-medium">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CINE SUNTEM */}
      <section className="py-14 md:py-20">
        <div className="container-x grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <span className="overline">{HOME.cineKicker}</span>
            <h2 className="mt-5 text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {HOME.cineTitle}
            </h2>
            <p className="mt-3 text-2xl md:text-3xl font-display font-medium text-brand">
              {HOME.cineSubtitle}
            </p>
            <div className="mt-6 max-w-xl space-y-4">
              {HOME.cineText.map((t, i) => (
                <p key={i} className="text-slate-600 leading-relaxed text-[17px]">
                  {t}
                </p>
              ))}
            </div>
            <Link
              to="/despre-noi"
              data-testid="home-about-link"
              className="link-underline mt-8 inline-flex items-center font-semibold text-ink hover:text-brand transition-colors"
            >
              {HOME.cineCta}
            </Link>
          </Reveal>

          <Reveal delay={0.12} className="relative">
            <div className="card overflow-hidden p-8 md:p-10">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex h-28 md:h-32 items-center justify-center rounded-2xl border border-slate-100 bg-gradient-to-br from-teal-50 to-white p-6">
                  <img src={ASSETS.isicColor} alt="ISIC România" className="max-h-14 md:max-h-16 w-auto max-w-[82%] object-contain" />
                </div>
                <div className="flex h-28 md:h-32 items-center justify-center rounded-2xl border border-slate-100 bg-gradient-to-br from-pink-50 to-white p-6">
                  <img src={ASSETS.eycaColor} alt="Cardul European de Tineret" className="max-h-14 md:max-h-16 w-auto max-w-[82%] object-contain" />
                </div>
                <div className="col-span-2 rounded-2xl bg-slate-900 p-7 text-white">
                  <div className="text-2xl font-extrabold">ISIC Jobs</div>
                  <p className="mt-2 text-slate-300">
                    Platforma care conectează tinerii cu angajatori, joburi și internshipuri.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CE FACEM */}
      <section className="relative overflow-hidden bg-white py-14 md:py-20 border-y border-slate-100">
        <MeshBg variant="warm" />
        <div className="container-x grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal className="order-1">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="absolute -inset-3 -z-10 rounded-[2rem] bg-gradient-to-tr from-indigo-100 to-emerald-100 blur-lg" />
              <img
                src={ASSETS.ceFacemPhoto}
                alt="Activare Făt-Frumos în campus"
                className="aspect-[4/3] w-full rounded-[1.6rem] object-cover shadow-[0_40px_80px_-40px_rgba(15,23,42,0.4)]"
              />
              <div className="absolute -bottom-5 right-3 rounded-2xl border border-slate-100 bg-white/90 px-5 py-4 shadow-xl backdrop-blur">
                <div className="text-2xl md:text-3xl font-extrabold text-brand">400.000+</div>
                <div className="text-xs font-medium text-slate-500">tineri în comunitate</div>
              </div>
            </div>
          </Reveal>
          <Reveal className="order-2">
            <span className="overline">Ce facem</span>
            <h2 className="mt-5 text-3xl md:text-5xl font-bold tracking-tight leading-[1.08]">
              {HOME.ceFacemTitle}
            </h2>
            <p className="mt-6 max-w-xl lead">{HOME.ceFacemText}</p>
          </Reveal>
        </div>

        {/* Două zone vizuale: Digital + În lumea reală */}
        <div className="container-x mt-14 grid gap-6 md:gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="card h-full p-8 md:p-10">
              <span className="overline">{HOME.digital.title}</span>
              <div className="mt-6 grid grid-cols-2 gap-5">
                {HOME.digital.stats.map((s, i) => (
                  <div key={i}>
                    <div className="text-3xl md:text-4xl font-extrabold tracking-tight text-brand">{s.value}</div>
                    <div className="mt-1 text-sm text-slate-500 font-medium">{s.label}</div>
                  </div>
                ))}
              </div>
              <ul className="mt-6 space-y-2.5 border-t border-slate-100 pt-6">
                {HOME.digital.items.map((it, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-slate-700 font-medium">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card h-full p-8 md:p-10">
              <span className="overline">{HOME.real.title}</span>
              <div className="mt-6 grid grid-cols-2 gap-6">
                {HOME.real.stats.map((s, i) => (
                  <div key={i}>
                    <div className="text-3xl md:text-4xl font-extrabold tracking-tight text-leaf-dark">{s.value}</div>
                    <div className="mt-1 text-sm text-slate-500 font-medium">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-14 md:py-20">
        <div className="container-x grid gap-6 md:gap-8 md:grid-cols-2">
          {HOME.services.map((s, i) => {
            const isLeaf = s.accent === "leaf";
            const Icon = isLeaf ? Megaphone : Sparkles;
            return (
              <Reveal key={s.title} delay={i * 0.1}>
                <Link
                  to={s.to}
                  data-testid={`home-service-${i}`}
                  className="group card flex h-full flex-col p-8 md:p-10 transition-transform duration-300 hover:scale-[1.02]"
                >
                  <div
                    className={
                      "inline-flex h-16 w-16 items-center justify-center rounded-2xl " +
                      (isLeaf ? "bg-emerald-50 text-leaf-dark" : "bg-indigo-50 text-brand")
                    }
                  >
                    <Icon className="h-8 w-8" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-3 flex-1 text-slate-600 leading-relaxed text-[17px]">{s.text}</p>
                  <span
                    className={
                      "mt-6 inline-flex items-center font-semibold " +
                      (isLeaf ? "text-leaf-dark" : "text-brand")
                    }
                  >
                    {s.cta}
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* BRANDS */}
      <section id="branduri" className="scroll-mt-24 bg-white py-14 md:py-20 border-t border-slate-100">
        <div className="container-x">
          <Reveal>
            <span className="overline">Portofoliu</span>
            <h2 className="mt-5 max-w-3xl text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {HOME.brandsTitle}
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-4 md:gap-6 md:grid-cols-3">
            {BRANDS.map((b, i) => (
              <Reveal key={b.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={`/${b.slug}`}
                  data-testid={`brand-${b.slug}`}
                  className="group flex h-36 md:h-44 items-center justify-center overflow-hidden rounded-3xl border border-slate-100 bg-white p-3 md:p-4 shadow-[0_8px_24px_-10px_rgba(15,23,42,0.15)] transition-all duration-300 hover:border-brand/30 hover:shadow-[0_24px_50px_-30px_rgba(108,92,231,0.5)]"
                >
                  <img
                    src={b.logo}
                    alt={b.name}
                    className="max-h-[92%] max-w-[92%] object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-brand px-8 py-14 md:px-16 md:py-20 text-center">
              <div className="pointer-events-none absolute -top-16 -left-10 h-64 w-64 rounded-full bg-white/15 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -right-10 h-72 w-72 rounded-full bg-black/10 blur-3xl" />
              <h2 className="relative mx-auto max-w-2xl text-3xl md:text-5xl font-extrabold tracking-tight text-white">
                {CAMPANII.ctaTitle}
              </h2>
              <div className="relative mt-8">
                <Link to="/contact" data-testid="home-cta-contact" className="btn px-8 py-4 bg-white text-ink hover:-translate-y-0.5">
                  {CAMPANII.ctaButton}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
